import type { Appearance, CreationCatalog, Sex } from '../api/types';
import { t } from '../i18n/idioma';
import { Paperdoll } from './Paperdoll';

interface Props {
  catalog: CreationCatalog;
  sex: Sex;
  value: Appearance;
  onChange: (next: Appearance) => void;
  onSex?: (sex: Sex) => void;
  lockBorn?: boolean;
  /** Criação não escolhe camisa nem calça. */
  roupa?: boolean;
  nome?: string;
  onNome?: (nome: string) => void;
}

export function AppearanceEditor({ catalog, sex, value, onChange, onSex, lockBorn, roupa = true, nome, onNome }: Props) {
  const hairs = sex === 'male' ? catalog.maleHair : catalog.femaleHair;
  const set = (patch: Partial<Appearance>) => onChange({ ...value, ...patch });

  return (
    <div className="look">
      <Paperdoll appearance={value} sex={sex} />
      <div className="stack">
        {onNome && (
          <label className="field">
            <span className="field__label">{t('comum.nome')}</span>
            <input
              value={nome ?? ''}
              onChange={e => onNome(e.target.value)}
              maxLength={16}
              autoComplete="off"
              spellCheck={false}
            />
          </label>
        )}
        {!lockBorn && onSex && (
          <div className="row">
            <button type="button" className="btn" aria-pressed={sex === 'male'} onClick={() => onSex('male')}>{t('personagem.homem')}</button>
            <button type="button" className="btn" aria-pressed={sex === 'female'} onClick={() => onSex('female')}>{t('personagem.mulher')}</button>
          </div>
        )}
        {!lockBorn && (
          <HueField label={t('personagem.pele')} min={catalog.skinHue.min} max={catalog.skinHue.max}
            value={value.skinHue} onChange={n => set({ skinHue: n })} />
        )}
        <label className="field">
          <span className="field__label">{t('personagem.cabelo')}</span>
          <select value={value.hairStyle} onChange={e => set({ hairStyle: e.target.value })}>
            {hairs.map(h => <option key={h.id} value={h.id}>{h.label}</option>)}
          </select>
        </label>
        <HueField label={t('personagem.corDoCabelo')} min={catalog.clothHue.min} max={catalog.clothHue.max}
          value={value.hairHue} onChange={n => set({ hairHue: n })} />
        {sex === 'male' && (
          <>
            <label className="field">
              <span className="field__label">{t('personagem.barba')}</span>
              <select
                value={value.beardStyle ?? 'none'}
                onChange={e => set({
                  beardStyle: e.target.value,
                  beardHue: e.target.value === 'none' ? null : (value.beardHue ?? catalog.clothHue.min)
                })}
              >
                {catalog.beards.map(b => <option key={b.id} value={b.id}>{b.label}</option>)}
              </select>
            </label>
            {value.beardStyle && value.beardStyle !== 'none' && (
              <HueField label={t('personagem.corDaBarba')} min={catalog.clothHue.min} max={catalog.clothHue.max}
                value={value.beardHue ?? catalog.clothHue.min} onChange={n => set({ beardHue: n })} />
            )}
          </>
        )}
        {roupa && (
          <>
            <HueField label={t('personagem.camisa')} min={catalog.clothHue.min} max={catalog.clothHue.max}
              value={value.shirtHue} onChange={n => set({ shirtHue: n })} />
            <HueField label={t('personagem.calca')} min={catalog.clothHue.min} max={catalog.clothHue.max}
              value={value.pantsHue} onChange={n => set({ pantsHue: n })} />
          </>
        )}
      </div>
    </div>
  );
}

function HueField({ label, min, max, value, onChange }: {
  label: string; min: number; max: number; value: number; onChange: (n: number) => void;
}) {
  return (
    <label className="field">
      <span className="field__label">{label}</span>
      <input type="range" min={min} max={max} value={value}
        onChange={e => onChange(Number(e.target.value))} />
    </label>
  );
}
